import Splide from '@splidejs/splide'
import L from 'leaflet'
import 'leaflet.markercluster'

import POIService, { POI_CATEGORIES, POICategory, POIData } from '../Service/POIService'
import { getElements } from '../Utility/Dom'
import * as SplideSlider from '../Utility/SplideSlider'
import { isValidJson } from '../Validation/Json'

export interface MapMarkerData {
  area?: number
  detailUrl?: string
  district?: string
  features?: MapMarkerFeature[]
  hasVirtualTour?: boolean
  id?: string
  imageCount?: number
  images?: MapMarkerImage[]
  latitude: number
  location?: string
  longitude: number
  noImageUrl?: string
  pinIconUrl?: string
  price?: number
  rooms?: number
  slug?: string
  thumbnail?: string
  title?: string
  virtualTour360IconUrl?: string
  virtualTourBgUrl?: string
  virtualTourTitle?: string
}

export interface MapMarkerFeature {
  key: string
  label: string
}

export interface MapMarkerImage {
  alt?: string
  title?: string
  url: string
}

class LocationMap {
  private readonly defaultPOIRadius: number = 1500 // meters
  private readonly defaultZoomLevel: number = 20
  private imageLoader: ((markerId: string) => Promise<MapMarkerImage[]>) | null = null
  private readonly items: NodeListOf<HTMLElement>
  private readonly mapIdStorage: Map<string, HTMLElement> = new Map()
  private readonly mapStorage: Map<HTMLElement, L.Map>
  private readonly markerGroupStorage: Map<HTMLElement, L.LayerGroup>
  private readonly maxZoom: number = 18
  private readonly multiMarkerZoomLevel: number = 13
  private readonly poiControlStorage: Map<HTMLElement, L.Control.Layers> = new Map()
  private readonly poiDataStorage: Map<HTMLElement, POIData[]> = new Map()
  private readonly poiLayerStorage: Map<HTMLElement, Map<string, L.MarkerClusterGroup>> = new Map()
  private readonly popupSliderStorage: Map<string, Splide> = new Map()
  private readonly singleMarkerCoords: Map<HTMLElement, { lat: number, lng: number }> = new Map()
  private readonly wolfsburgLat: number = 52.4227
  private readonly wolfsburgLng: number = 10.7865
  private readonly wolfsburgZoom: number = 12

  constructor () {
    this.items = getElements('map')
    this.mapStorage = new Map()
    this.markerGroupStorage = new Map()
  }

  public destroyMap (mapElement: HTMLElement): void {
    this.onConsentDeclined(mapElement)
  }

  public getPOICategories (): POICategory[] {
    return POI_CATEGORIES
  }

  public getPOIs (mapElement: HTMLElement): POIData[] {
    return this.poiDataStorage.get(mapElement) || []
  }

  public hideAllPOIs (mapElement: HTMLElement): void {
    for (const category of POI_CATEGORIES) {
      this.togglePOICategory(mapElement, category.key, false)
    }
  }

  public init (): void {
    if (this.items.length === 0) return

    this.addEvents()
  }

  public initializeMapElement (mapElement: HTMLElement): void {
    mapElement.addEventListener('consentAccepted', (): void => this.onConsentAccept(mapElement))
    mapElement.addEventListener('consentDeclined', (): void => this.onConsentDeclined(mapElement))
  }

  public async loadPOIs (
    mapElement: HTMLElement,
    latitude: number,
    longitude: number,
    radius: number = this.defaultPOIRadius,
    categories?: string[]
  ): Promise<void> {
    const map = this.mapStorage.get(mapElement)

    if (!map) {
      console.warn('LocationMap: Map not initialized, cannot load POIs')
      return
    }

    try {
      const pois = await POIService.fetchPOIs({
        categories,
        latitude,
        longitude,
        radius
      })

      if (pois.length === 0) {
        return
      }

      this.poiDataStorage.set(mapElement, pois)
      this.initializePOILayers(mapElement, map)
      this.addPOIMarkers(mapElement, map, pois)

      // Dispatch event when POIs are loaded
      const poiLoadedEvent = new CustomEvent('poisLoaded', {
        detail: { categories: this.getLoadedCategories(pois), count: pois.length }
      })
      mapElement.dispatchEvent(poiLoadedEvent)
    } catch (error) {
      console.error('LocationMap: Failed to load POIs', error)
    }
  }

  public setImageLoader (loader: (markerId: string) => Promise<MapMarkerImage[]>): void {
    this.imageLoader = loader
  }

  public showAllPOIs (mapElement: HTMLElement): void {
    for (const category of POI_CATEGORIES) {
      this.togglePOICategory(mapElement, category.key, true)
    }
  }

  public togglePOICategory (mapElement: HTMLElement, categoryKey: string, visible: boolean): void {
    const map = this.mapStorage.get(mapElement)
    const poiLayers = this.poiLayerStorage.get(mapElement)

    if (!map || !poiLayers) {
      return
    }

    const layerGroup = poiLayers.get(categoryKey)

    if (layerGroup) {
      if (visible) {
        map.addLayer(layerGroup)
      } else {
        map.removeLayer(layerGroup)
      }
    }
  }

  public updateMapData (mapElement: HTMLElement, markers: MapMarkerData[]): void {
    const map = this.mapStorage.get(mapElement)

    if (!map) {
      console.warn('LocationMap: Map not initialized, cannot update data')
      return
    }

    // Remove all layers except the tile layer
    map.eachLayer((layer) => {
      if (!(layer instanceof L.TileLayer)) {
        map.removeLayer(layer)
      }
    })

    const markerGroup = L.layerGroup().addTo(map)
    this.markerGroupStorage.set(mapElement, markerGroup)

    const dataJson = JSON.stringify(markers)
    this.createMultiMarkerMap(map, markerGroup, dataJson)
  }

  private addEvents (): void {
    this.items.forEach((map): void => {
      map.addEventListener('consentAccepted', (): void => this.onConsentAccept(map))
      map.addEventListener('consentDeclined', (): void => this.onConsentDeclined(map))
    })
  }

  private addPOIMarkers (mapElement: HTMLElement, map: L.Map, pois: POIData[]): void {
    const poiLayers = this.poiLayerStorage.get(mapElement)

    if (!poiLayers) {
      return
    }

    for (const poi of pois) {
      const layerGroup = poiLayers.get(poi.categoryKey)
      const category = POIService.getCategory(poi.categoryKey)

      if (layerGroup && category) {
        const marker = this.createPOIMarker(poi, category)
        layerGroup.addLayer(marker)
      }
    }
  }

  private addSimpleMarker (
    markerGroup: L.LayerGroup,
    latitude: number,
    longitude: number,
    title: string = '',
    autoClose: boolean = true
  ): void {
    const marker = L.marker([latitude, longitude], {
      icon: L.icon({
        iconAnchor: [17, 51],
        iconSize: [35, 51],
        iconUrl: '/_assets/7ef8d23760116a74c8e82edfcb4c7bbe/Icons/pin.svg',
        popupAnchor: [0, -51],
        shadowSize: [0, 0]
      }),
      title
    }).addTo(markerGroup)

    if (title !== '') {
      marker.bindPopup(title, { autoClose })
      marker.openPopup()
    }
  }

  private buildVirtualTourSlideHtml (markerData: MapMarkerData): string {
    const parts: string[] = []
    const title = markerData.virtualTourTitle

    parts.push('<div class="immo-map-popup__virtual-tour">')
    parts.push(`<img src="${this.escapeHtml(markerData.virtualTourBgUrl ?? '')}" alt="" class="immo-map-popup__image immo-map-popup__image--virtual-tour" loading="lazy">`)
    parts.push('<div class="immo-map-popup__virtual-tour-content">')

    if (title) {
      parts.push(`<p class="immo-map-popup__virtual-tour-title">${this.escapeHtml(title)}</p>`)
    }

    if (markerData.virtualTour360IconUrl) {
      parts.push(`<img src="${this.escapeHtml(markerData.virtualTour360IconUrl)}" alt="" class="immo-map-popup__virtual-tour-icon" loading="lazy">`)
    }

    parts.push('</div>')
    parts.push('</div>')

    return parts.join('')
  }

  private clearPOILayers (mapElement: HTMLElement): void {
    const map = this.mapStorage.get(mapElement)
    const poiLayers = this.poiLayerStorage.get(mapElement)
    const layerControl = this.poiControlStorage.get(mapElement)

    if (map && poiLayers) {
      for (const layerGroup of poiLayers.values()) {
        map.removeLayer(layerGroup)
        layerGroup.clearLayers()
      }
    }

    if (map && layerControl) {
      map.removeControl(layerControl)
    }

    this.poiLayerStorage.delete(mapElement)
    this.poiControlStorage.delete(mapElement)
    this.poiDataStorage.delete(mapElement)
  }

  private createLayerLabel (category: POICategory): string {
    return `<span class="leaflet__layer-label">
      <img src="${category.icon}" alt="" class="leaflet__layer-icon" width="14" height="14">
      ${category.label}
    </span>`
  }

  private createMap (mapItem: HTMLElement): void {
    const mapId = mapItem.getAttribute('id')
    if (!mapId) {
      console.warn('LocationMap: No map ID found')
      return
    }

    // Check if a map with this ID already exists
    const existingElement = this.mapIdStorage.get(mapId)
    if (existingElement && this.mapStorage.has(existingElement)) {
      if (!document.contains(existingElement)) {
        const oldMap = this.mapStorage.get(existingElement)
        if (oldMap) {
          oldMap.remove()
        }

        this.mapStorage.delete(existingElement)
        this.markerGroupStorage.delete(existingElement)
      } else {
        return
      }
    }

    if (this.mapStorage.has(mapItem)) {
      return
    }

    this.mapIdStorage.set(mapId, mapItem)

    const map = L.map(mapId, {
      scrollWheelZoom: true,
      zoomControl: true
    })

    const markerGroup = L.layerGroup().addTo(map)
    this.mapStorage.set(mapItem, map)
    this.markerGroupStorage.set(mapItem, markerGroup)

    // Add OpenStreetMap tiles
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" rel="noopener">OpenStreetMap</a> contributors',
      maxZoom: this.maxZoom
    }).addTo(map)

    // Check for multi-marker data first (data-immobilien for Immobilien list)
    const multiMarkerData = mapItem.getAttribute('data-immobilien')
    if (multiMarkerData && isValidJson(multiMarkerData)) {
      this.createMultiMarkerMap(map, markerGroup, multiMarkerData)
    } else {
      this.createSingleMarkerMap(map, markerGroup, mapItem)
    }

    // Invalidate size after DOM is ready - multiple attempts to ensure proper rendering
    setTimeout((): void => {
      map.invalidateSize()
    }, 100)

    // Additional invalidateSize calls for better reliability
    setTimeout((): void => {
      map.invalidateSize()
    }, 300)

    setTimeout((): void => {
      map.invalidateSize()

      const mapLoadedEvent = new Event('mapLoaded')
      mapItem.dispatchEvent(mapLoadedEvent)

      const coords = this.singleMarkerCoords.get(mapItem)
      if (coords) {
        this.initializePOILabels(mapItem)
        this.loadPOIs(mapItem, coords.lat, coords.lng)
      }
    }, 600)
  }

  /**
   * Create map with multiple markers and clustering
   */
  private createMultiMarkerMap (map: L.Map, markerGroup: L.LayerGroup, dataJson: string): void {
    const markers: MapMarkerData[] = JSON.parse(dataJson)

    if (!markers || markers.length === 0) {
      console.warn('LocationMap: No markers to display')
      map.setView([this.wolfsburgLat, this.wolfsburgLng], this.wolfsburgZoom)
      return
    }

    const clusterGroup = L.markerClusterGroup({
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount()
        let sizeClass = 'marker-cluster-small'

        if (count >= 10) {
          sizeClass = 'marker-cluster-large'
        } else if (count >= 5) {
          sizeClass = 'marker-cluster-medium'
        }

        return L.divIcon({
          className: `marker-cluster ${sizeClass}`,
          html: `<div><span>${count}</span></div>`,
          iconSize: L.point(40, 40)
        })
      },
      maxClusterRadius: 80,
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      zoomToBoundsOnClick: true
    })

    const bounds: L.LatLngBounds = L.latLngBounds([])
    let validMarkerCount = 0

    markers.forEach((markerData): void => {
      if (!this.hasValidCoordinates(markerData)) {
        console.warn('LocationMap: Invalid coordinates for marker', markerData.id || markerData.title)
        return
      }

      const marker = this.createRichMarker(markerData)
      clusterGroup.addLayer(marker)
      bounds.extend(marker.getLatLng())
      validMarkerCount++
    })

    map.addLayer(clusterGroup)

    // Fit map to bounds
    if (validMarkerCount > 0) {
      if (validMarkerCount === 1) {
        // Single marker: center with default zoom
        map.setView(bounds.getCenter(), this.multiMarkerZoomLevel)
      } else {
        // Multiple markers: fit bounds with padding
        map.fitBounds(bounds, {
          maxZoom: this.multiMarkerZoomLevel,
          padding: [50, 50]
        })
      }
    } else {
      // No valid markers: default Wolfsburg view
      map.setView([this.wolfsburgLat, this.wolfsburgLng], this.wolfsburgZoom)
    }
  }

  private createPOIMarker (poi: POIData, category: POICategory): L.Marker {
    const icon = L.divIcon({
      className: 'poi-marker-container',
      html: `<div class="leaflet__marker leaflet__marker--poi">
        <img src="${category.icon}" alt="${category.label}" class="leaflet__marker-icon">
      </div>`,
      iconAnchor: category.iconAnchor,
      iconSize: category.iconSize,
      popupAnchor: [0, -32]
    })

    const marker = L.marker([poi.latitude, poi.longitude], {
      icon,
      title: poi.name
    })

    marker.bindPopup(this.createPOIPopupContent(poi, category), {
      className: 'leaflet__poi',
      maxWidth: 280
    })

    return marker
  }

  private createPOIPopupContent (poi: POIData, category: POICategory): string {
    const parts: string[] = []

    parts.push('<div class="leaflet__popup">')
    parts.push(`<div class="leaflet__popup-category">${this.escapeHtml(category.label)}</div>`)
    parts.push(`<div class="leaflet__popup-title">${this.escapeHtml(poi.name)}</div>`)

    if (poi.address) {
      parts.push(`<address class="leaflet__popup-address">${this.escapeHtml(poi.address)}</address>`)
    }

    if (poi.distance) {
      const distanceText = poi.distance >= 1000
        ? `${(poi.distance / 1000).toFixed(1)} km`
        : `${poi.distance} m`
      parts.push(`<div class="leaflet__popup-distance">${POIService.getDistanceLabel()}: ${distanceText}</div>`)
    }

    if (poi.openingHours) {
      parts.push(`<div class="leaflet__popup-hours">${this.escapeHtml(poi.openingHours)}</div>`)
    }

    parts.push('</div>')

    return parts.join('')
  }

  private createPopupContent (markerData: MapMarkerData, popupId: string): string {
    const parts: string[] = []

    parts.push('<div class="immo-map-popup__card">')

    if (markerData.detailUrl) {
      parts.push(`<a href="${this.escapeHtml(markerData.detailUrl)}" class="immo-map-popup__link">`)
      parts.push(`<span class="visually-hidden">${this.escapeHtml(markerData.title || 'Immobilie')}</span>`)
      parts.push('</a>')
    }

    const hasVirtualTour = !!(markerData.hasVirtualTour && markerData.virtualTourBgUrl)
    const virtualTourOffset = hasVirtualTour ? 1 : 0
    const imageCount = markerData.images?.length ?? 0
    const totalSlideCount = (markerData.imageCount ?? imageCount) + virtualTourOffset
    const renderableSlideCount = imageCount + virtualTourOffset

    if (renderableSlideCount > 1) {
      // Multiple slides (virtual tour + at least one image, or multiple images) — build slider
      parts.push(`<div id="${popupId}" class="immo-map-popup__slider splide">`)
      parts.push('<div class="splide__track">')
      parts.push('<ul class="splide__list">')

      if (hasVirtualTour) {
        parts.push('<li class="splide__slide">')
        parts.push(this.buildVirtualTourSlideHtml(markerData))
        parts.push('</li>')
      }

      markerData.images?.forEach((image) => {
        parts.push('<li class="splide__slide">')
        parts.push(`<img src="${this.escapeHtml(image.url)}" alt="${this.escapeHtml(image.alt || markerData.title || '')}" class="immo-map-popup__image" loading="lazy">`)
        parts.push('</li>')
      })

      parts.push('</ul>')
      parts.push('</div>')

      parts.push('<div class="splide__controls splide__controls--overlay">')
      parts.push('<div class="splide__arrows splide__arrows--overlay">')
      parts.push('<button aria-label="Vorheriges Bild" class="splide__arrow splide__arrow--aside splide__arrow--prev" type="button"></button>')
      parts.push('<button aria-label="Nächstes Bild" class="splide__arrow splide__arrow--aside splide__arrow--next" type="button"></button>')
      parts.push('</div>')
      parts.push('</div>')

      parts.push('<div class="overlay-btn overlay-btn--pagination overlay-btn--pagination-small">')
      parts.push(`<span class="immo-map-popup__counter-text">1/${totalSlideCount}</span>`)
      parts.push('</div>')

      parts.push('</div>')
    } else if (renderableSlideCount === 1 && hasVirtualTour) {
      // Only virtual tour, no real images
      parts.push('<div class="immo-map-popup__image-container">')
      parts.push(this.buildVirtualTourSlideHtml(markerData))
      parts.push('</div>')
    } else if (markerData.images && markerData.images.length === 1) {
      // Single image - no slider
      parts.push('<div class="immo-map-popup__image-container">')
      parts.push(`<img src="${this.escapeHtml(markerData.images[0].url)}" alt="${this.escapeHtml(markerData.images[0].alt || markerData.title || '')}" class="immo-map-popup__image" loading="lazy">`)

      if (markerData.imageCount && markerData.imageCount > 1) {
        parts.push('<div class="immo-map-popup__image-count">')
        parts.push(`<span class="immo-map-popup__image-count-text">1 / ${markerData.imageCount}</span>`)
        parts.push('</div>')
      }

      parts.push('</div>')
    } else if (markerData.thumbnail) {
      // Fallback to thumbnail
      parts.push('<div class="immo-map-popup__image-container">')
      parts.push(`<img src="${this.escapeHtml(markerData.thumbnail)}" alt="${this.escapeHtml(markerData.title || '')}" class="immo-map-popup__image" loading="lazy">`)
      parts.push('</div>')
    } else if (markerData.noImageUrl) {
      // No image available - show placeholder
      parts.push('<div class="immo-map-popup__no-image">')
      parts.push(`<img src="${this.escapeHtml(markerData.noImageUrl)}" alt="" class="immo-map-popup__no-image-icon">`)
      parts.push('<span class="immo-map-popup__no-image-text">Kein Bild verfügbar</span>')
      parts.push('</div>')
    }

    // Content container
    parts.push('<div class="immo-map-popup__content">')

    // Overline with district (if available)
    if (markerData.district) {
      parts.push('<div class="immo-map-popup__overline">')
      parts.push(`<span>${this.escapeHtml(markerData.district)}</span>`)
      parts.push('</div>')
    }

    // Title
    parts.push(`<h3 class="immo-map-popup__title">${this.escapeHtml(markerData.title || 'Immobilie')}</h3>`)

    // Features/Tags
    if (markerData.area || markerData.rooms || (markerData.features && markerData.features.length > 0)) {
      parts.push('<div class="immo-map-popup__features">')

      if (markerData.area) {
        parts.push(`<span class="tag tag--area tag--icon tag--main">${markerData.area} m²</span>`)
      }

      if (markerData.rooms) {
        parts.push(`<span class="tag tag--icon tag--main tag--rooms">${markerData.rooms} Zimmer</span>`)
      }

      if (markerData.features) {
        markerData.features.forEach((feature) => {
          parts.push(`<span class="tag tag--icon tag--main" data-ausstattung="${this.escapeHtml(feature.key)}">${this.escapeHtml(feature.label)}</span>`)
        })
      }

      parts.push('</div>')
    }

    // Price
    if (markerData.price) {
      parts.push('<div class="immo-map-popup__price-container">')
      parts.push(`<span class="immo-map-popup__price">${this.formatPrice(markerData.price)}</span>`)
      parts.push('<span class="immo-map-popup__price-label">Gesamtmiete</span>')
      parts.push('</div>')
    }

    parts.push('</div>') // Close content

    parts.push('</div>') // Close card

    return parts.join('')
  }

  /**
   * Create rich marker with detailed popup (for Immobilien)
   * Returns marker without adding to map (for clustering)
   */
  private createRichMarker (markerData: MapMarkerData): L.Marker {
    // Generate stable popup ID once - used for content creation, slider init, and cleanup
    const popupId = `popup-${markerData.id || Math.random().toString(36).substring(2, 11)}`

    const marker = L.marker([markerData.latitude, markerData.longitude], {
      icon: L.icon({
        iconAnchor: [17, 51],
        iconSize: [35, 51],
        iconUrl: markerData.pinIconUrl || '/_assets/7ef8d23760116a74c8e82edfcb4c7bbe/Icons/pin.svg',
        popupAnchor: [0, -51],
        shadowSize: [0, 0]
      }),
      title: markerData.title || ''
    })

    // Create popup content with stable ID
    const popupContent = this.createPopupContent(markerData, popupId)
    marker.bindPopup(popupContent, {
      className: 'immo-map-popup',
      maxWidth: 320
    })

    // Initialize slider or lazy-load images when popup opens
    marker.on('popupopen', () => {
      if (markerData.imageCount && markerData.imageCount > 1 &&
          (!markerData.images || markerData.images.length <= 1) &&
          this.imageLoader && markerData.id) {
        void this.lazyLoadPopupImages(marker, markerData, popupId)
      } else {
        this.initializePopupSlider(markerData, popupId)
      }
    })

    // Destroy slider when popup closes (using stable ID)
    marker.on('popupclose', () => {
      this.destroyPopupSlider(popupId)
    })

    return marker
  }

  private createSingleMarkerMap (map: L.Map, markerGroup: L.LayerGroup, mapItem: HTMLElement): void {
    const mapData: string = mapItem.getAttribute('data-content')
    if (!mapData || !isValidJson(mapData)) {
      console.warn('LocationMap: No valid single marker data found')
      return
    }

    const mapDataObject = JSON.parse(mapData)
    const latitude: number = Number(mapDataObject.latitude || 0)
    const longitude: number = Number(mapDataObject.longitude || 0)
    const title: string = String(mapDataObject.title || '')

    if (latitude === 0 || longitude === 0) {
      console.warn('LocationMap: Invalid coordinates for single marker')
      return
    }

    // Store coordinates for automatic POI loading
    this.singleMarkerCoords.set(mapItem, { lat: latitude, lng: longitude })

    map.setView([latitude, longitude], this.defaultZoomLevel)
    this.addSimpleMarker(markerGroup, latitude, longitude, title)
  }

  private destroyPopupSlider (popupId: string): void {
    const splide = this.popupSliderStorage.get(popupId)

    if (splide) {
      // Delay destruction to allow popup close animation to complete
      // This prevents all slides from being visible during the fade-out
      setTimeout(() => {
        splide.destroy()
        this.popupSliderStorage.delete(popupId)
      }, 250)
    }
  }

  private escapeHtml (text: string): string {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  private formatPrice (price: number): string {
    return new Intl.NumberFormat('de-DE', {
      currency: 'EUR',
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
      style: 'currency'
    }).format(price)
  }

  private getLoadedCategories (pois: POIData[]): string[] {
    return [...new Set(pois.map(p => p.categoryKey))]
  }

  private hasValidCoordinates (markerData: MapMarkerData): boolean {
    return (
      typeof markerData.latitude === 'number' &&
      typeof markerData.longitude === 'number' &&
      !isNaN(markerData.latitude) &&
      !isNaN(markerData.longitude) &&
      markerData.latitude !== 0 &&
      markerData.longitude !== 0 &&
      markerData.latitude >= -90 &&
      markerData.latitude <= 90 &&
      markerData.longitude >= -180 &&
      markerData.longitude <= 180
    )
  }

  private initializePOILabels (mapElement: HTMLElement): void {
    const labelsData = mapElement.getAttribute('data-poi-labels')
    if (labelsData && isValidJson(labelsData)) {
      const labels = JSON.parse(labelsData)
      POIService.setLabels(labels)
    }
  }

  private initializePOILayers (mapElement: HTMLElement, map: L.Map): void {
    this.clearPOILayers(mapElement)
    const poiLayers = new Map<string, L.MarkerClusterGroup>()
    const overlays: Record<string, L.MarkerClusterGroup> = {}

    for (const category of POI_CATEGORIES) {
      const clusterGroup = L.markerClusterGroup({
        disableClusteringAtZoom: 18,
        iconCreateFunction: (cluster) => {
          const count = cluster.getChildCount()
          let sizeClass = 'marker-cluster-small'

          if (count >= 10) {
            sizeClass = 'marker-cluster-large'
          } else if (count >= 5) {
            sizeClass = 'marker-cluster-medium'
          }

          return L.divIcon({
            className: `marker-cluster ${sizeClass}`,
            html: `<div><span>${count}</span></div>`,
            iconSize: L.point(40, 40)
          })
        },
        maxClusterRadius: 50,
        showCoverageOnHover: false,
        spiderfyOnMaxZoom: true,
        zoomToBoundsOnClick: true
      })

      poiLayers.set(category.key, clusterGroup)
      overlays[this.createLayerLabel(category)] = clusterGroup
    }

    this.poiLayerStorage.set(mapElement, poiLayers)

    // Add layer control
    const layerControl = L.control.layers({}, overlays, {
      collapsed: true,
      position: 'topright'
    }).addTo(map)

    this.poiControlStorage.set(mapElement, layerControl)
  }

  private initializePopupSlider (markerData: MapMarkerData, popupId: string): void {
    const virtualTourOffset = markerData.hasVirtualTour && markerData.virtualTourBgUrl ? 1 : 0
    const totalSlides = (markerData.images?.length ?? 0) + virtualTourOffset

    if (totalSlides <= 1) {
      return
    }

    const sliderElement = document.getElementById(popupId)

    if (!sliderElement) {
      console.warn('LocationMap: Slider element not found', popupId)
      return
    }

    // Check if already initialized
    if (this.popupSliderStorage.has(popupId)) {
      return
    }

    const splide = SplideSlider.initialize(sliderElement)

    this.popupSliderStorage.set(popupId, splide)

    SplideSlider.setupCounter(
      sliderElement,
      splide,
      totalSlides,
      '.immo-map-popup__counter-text'
    )
  }

  private async lazyLoadPopupImages (marker: L.Marker, markerData: MapMarkerData, popupId: string): Promise<void> {
    if (!this.imageLoader || !markerData.id) return

    try {
      const images = await this.imageLoader(markerData.id)
      if (images.length > 1) {
        markerData.images = images
        markerData.imageCount = images.length
        const newContent = this.createPopupContent(markerData, popupId)
        marker.getPopup()?.setContent(newContent)
        this.initializePopupSlider(markerData, popupId)
      }
    } catch {
      // Silently keep the single titelbild on error
    }
  }

  private onConsentAccept (mapItem: HTMLElement): void {
    this.createMap(mapItem)
  }

  private onConsentDeclined (mapItem: HTMLElement): void {
    if (this.mapStorage.has(mapItem)) {
      this.clearPOILayers(mapItem)

      const map = this.mapStorage.get(mapItem)
      if (map) {
        map.remove()
      }
      this.mapStorage.delete(mapItem)
      this.markerGroupStorage.delete(mapItem)
      this.singleMarkerCoords.delete(mapItem)
    }
  }
}

const map = new LocationMap()
map.init()

export default map
